import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { useNotification } from './NotificationSystem';
import './ChannelPermissions.css';

const CHANNEL_PERMISSIONS = {
  VIEW_CHANNEL: { bit: 64, label: 'View Channel', description: 'Allow members to view this channel' },
  SEND_MESSAGES: { bit: 128, label: 'Send Messages', description: 'Allow members to send messages in this channel' },
  EDIT_MESSAGES: { bit: 256, label: 'Edit Messages', description: 'Allow members to edit their own messages' },
  DELETE_MESSAGES: { bit: 512, label: 'Delete Messages', description: 'Allow members to delete other\'s messages' },
  ADD_REACTIONS: { bit: 1024, label: 'Add Reactions', description: 'Allow members to add reactions to messages' },
  MENTION_EVERYONE: { bit: 2048, label: 'Mention @everyone', description: 'Allow members to use @everyone' },
  CONNECT_VOICE: { bit: 4096, label: 'Connect to Voice', description: 'Allow members to connect to voice channel' },
  SPEAK: { bit: 8192, label: 'Speak', description: 'Allow members to speak in voice channel' },
  MUTE_MEMBERS: { bit: 16384, label: 'Mute Members', description: 'Allow members to mute others in voice' },
  MOVE_MEMBERS: { bit: 32768, label: 'Move Members', description: 'Allow members to move others between voice channels' },
};

function ChannelPermissions({ channel, server, token, onClose }) {
  const { showSuccess, showError, confirm } = useNotification();
  const [roles, setRoles] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null); // { type: 'role'/'user', id, name }
  const [permissions, setPermissions] = useState({ allow: 0, deny: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedTarget) {
      fetchPermissions();
    }
  }, [selectedTarget]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/servers/${server.id}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
      if (response.data.length > 0) {
        setSelectedTarget({ type: 'role', id: response.data[0].id, name: response.data[0].name });
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const params = selectedTarget.type === 'role'
        ? { roleId: selectedTarget.id }
        : { userId: selectedTarget.id };
        
      const response = await axios.get(`${API_URL}/api/channels/${channel.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setPermissions({
        allow: response.data.allow_permissions || 0,
        deny: response.data.deny_permissions || 0
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching permissions:', error);
      }
      setPermissions({ allow: 0, deny: 0 });
    }
  };

  const hasPermission = (perm, bit) => {
    return (perm & bit) === bit;
  };

  const togglePermission = (bit, state) => {
    setPermissions(prev => {
      let newAllow = prev.allow;
      let newDeny = prev.deny;

      if (state === 'allow') {
        newAllow = prev.allow | bit;
        newDeny = prev.deny & ~bit;
      } else if (state === 'deny') {
        newDeny = prev.deny | bit;
        newAllow = prev.allow & ~bit;
      } else {
        // neutral - remove from both
        newAllow = prev.allow & ~bit;
        newDeny = prev.deny & ~bit;
      }

      return { allow: newAllow, deny: newDeny };
    });
  };

  const getPermissionState = (bit) => {
    if (hasPermission(permissions.allow, bit)) return 'allow';
    if (hasPermission(permissions.deny, bit)) return 'deny';
    return 'neutral';
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        allowPermissions: permissions.allow,
        denyPermissions: permissions.deny
      };

      if (selectedTarget.type === 'role') {
        payload.roleId = selectedTarget.id;
      } else {
        payload.userId = selectedTarget.id;
      }

      await axios.put(`${API_URL}/api/channels/${channel.id}/permissions`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSuccess('Permissions saved successfully');
    } catch (error) {
      console.error('Error saving permissions:', error);
      showError('Failed to save permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!await confirm('Reset permissions for this target?', 'Reset Permissions')) return;
    
    setLoading(true);
    try {
      const params = selectedTarget.type === 'role'
        ? { roleId: selectedTarget.id }
        : { userId: selectedTarget.id };

      await axios.delete(`${API_URL}/api/channels/${channel.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setPermissions({ allow: 0, deny: 0 });
      showSuccess('Permissions reset successfully');
    } catch (error) {
      console.error('Error resetting permissions:', error);
      showError('Failed to reset permissions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="channel-permissions-backdrop">
      <div className="channel-permissions-modal">
        <div className="channel-permissions-header">
          <h2>Channel Permissions - #{channel.name}</h2>
          <button className="channel-permissions-close" onClick={onClose}>×</button>
        </div>

        <div className="channel-permissions-content">
          <div className="channel-permissions-sidebar">
            <h3>Roles</h3>
            <div className="permission-targets">
              {roles.map(role => (
                <div
                  key={role.id}
                  className={`permission-target ${selectedTarget?.id === role.id ? 'active' : ''}`}
                  onClick={() => setSelectedTarget({ type: 'role', id: role.id, name: role.name })}
                >
                  <div className="target-color" style={{ backgroundColor: role.color || '#99aab5' }}></div>
                  <span>{role.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="channel-permissions-main">
            {selectedTarget && (
              <>
                <div className="permission-target-header">
                  <h3>Permissions for {selectedTarget.name}</h3>
                  <p className="permission-hint">
                    🟢 Allowed | ⚫ Neutral | 🔴 Denied
                  </p>
                </div>

                <div className="permissions-list">
                  {Object.entries(CHANNEL_PERMISSIONS).map(([key, perm]) => {
                    // Filter voice permissions for text channels
                    if (channel.type === 'text' && ['CONNECT_VOICE', 'SPEAK', 'MUTE_MEMBERS', 'MOVE_MEMBERS'].includes(key)) {
                      return null;
                    }
                    // Filter text permissions for voice channels
                    if (channel.type === 'voice' && ['SEND_MESSAGES', 'EDIT_MESSAGES', 'DELETE_MESSAGES', 'ADD_REACTIONS', 'MENTION_EVERYONE'].includes(key)) {
                      return null;
                    }

                    const state = getPermissionState(perm.bit);

                    return (
                      <div key={key} className="permission-item">
                        <div className="permission-info">
                          <div className="permission-label">{perm.label}</div>
                          <div className="permission-description">{perm.description}</div>
                        </div>
                        <div className="permission-toggle">
                          <button
                            className={`perm-btn ${state === 'allow' ? 'active-allow' : ''}`}
                            onClick={() => togglePermission(perm.bit, state === 'allow' ? 'neutral' : 'allow')}
                            title="Allow"
                          >
                            ✓
                          </button>
                          <button
                            className={`perm-btn ${state === 'deny' ? 'active-deny' : ''}`}
                            onClick={() => togglePermission(perm.bit, state === 'deny' ? 'neutral' : 'deny')}
                            title="Deny"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="permission-actions">
                  <button className="btn-reset" onClick={handleReset} disabled={loading}>
                    Reset Overrides
                  </button>
                  <button className="btn-save" onClick={handleSave} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChannelPermissions;
